import React, { useEffect, useState, createElement } from 'react';
import { ArrowRightIcon, ShieldCheckIcon, DatabaseIcon, RefreshCwIcon, ActivityIcon, PuzzleIcon, HardDriveIcon, EyeIcon, GaugeIcon, ZapIcon, LockIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function HostingOnderhoudPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Hosting & onderhoud | Zorgeloos online | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Website hosting en onderhoud zonder zorgen. Snelle servers, dagelijkse backups, beveiligingsupdates en 24/7 monitoring. Noord Digital ontzorgt je volledig.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Hosting & onderhoud | Zorgeloos online | Noord',
      'og:description': 'Website hosting en onderhoud zonder zorgen. Snelle servers, dagelijkse backups en 24/7 monitoring.',
      'og:type': 'website',
      'og:url': 'https://noorddigital.nl/hosting-onderhoud',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  const scrollToSection = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();
    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const faqs = [{
    q: 'Heb ik website onderhoud nodig?',
    a: 'Ja, elke website heeft onderhoud nodig. WordPress draait op software die regelmatig wordt bijgewerkt. Zonder updates loop je risico op beveiligingslekken, trage laadtijden en compatibiliteitsproblemen. Met ons onderhoudspakket hoef je je hier geen zorgen over te maken.'
  }, {
    q: 'Hoe vaak wordt mijn website geüpdatet?',
    a: 'Wij controleren je website wekelijks op beschikbare updates. Kritieke beveiligingsupdates worden direct doorgevoerd. Reguliere plugin- en thema-updates voeren wij maandelijks uit, altijd na een grondige test.'
  }, {
    q: 'Wat gebeurt er bij problemen?',
    a: 'Bij problemen grijpen wij direct in. Dankzij onze monitoring detecteren we issues vaak voordat jij ze merkt. Mocht er toch iets misgaan, dan herstellen we je website snel vanuit een recente backup. Je kunt ons altijd bereiken voor ondersteuning.'
  }];
  return <main className="pt-16 lg:pt-[72px]" data-id="element-1096" data-mp="1q7kwy5a6pfo7">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-1097" data-mp="1m9euvakajo9m">
        <div className="absolute inset-0 pointer-events-none" data-id="element-1098" data-mp="1h87l55yl8f0b">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-1099" data-mp="1c9wcgyk14ajo" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-1100" data-mp="17bcoytpseqp7" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-1101" data-mp="12j1nvnjb8yv2" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1102" data-mp="okl6n2ntnpyo">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-1103" data-mp="jma1344k81m9">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-1104" data-mp="2joszwc0t059b">
              Home
            </a>
            <span className="text-navy-500" data-id="element-1105" data-mp="29xybb1034tkp">/</span>
            <span className="text-accent-light font-medium" data-id="element-1106" data-mp="29x5cqnba5adn">
              Hosting & onderhoud
            </span>
          </nav>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up" data-id="element-1107" data-mp="2euf1e0quhi0f">
            Hosting en onderhoud zonder zorgen
          </h1>
          <p className="text-lg text-slate-600 text-center max-w-2xl mx-auto mb-12 lg:mb-16" data-id="element-1108" data-mp="25xegi42zi21q">
            Snelle servers, dagelijkse back-ups en geen omkijken naar updates of
            technisch gedoe. Wij regelen het.
          </p>
          <div className="animate-fade-in-up-delay-2" data-id="element-1109" data-mp="3lctzpeoja9eg">
            <a href="#pakketten" onClick={e => scrollToSection(e, 'pakketten')} className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover text-white font-bold px-8 py-4 rounded-button shadow-medium hover:shadow-card transition-all duration-200 text-lg" data-id="element-1110" data-mp="3ldd9u97xzthx">
              Bekijk onderhoudspakketten
              <ArrowRightIcon className="w-5 h-5" data-id="element-1111" data-mp="1oeo27vn1vthr" />
            </a>
          </div>
        </div>
      </section>

      {/* Waarom onderhoud belangrijk is */}
      <section className="py-24 bg-surface-muted" data-id="element-1112" data-mp="1jluxv8vhun67">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1113" data-mp="1xe1eew2k6w66">
          <div className="text-center mb-16" data-id="element-1114" data-mp="1tdr97lmlc9sd">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1115" data-mp="1tcpo0obv26wx">
              Waarom website onderhoud belangrijk is
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1116" data-mp="22e5tchntjiua">
              Een website zonder onderhoud is als een auto zonder APK. Het gaat
              een tijdje goed, maar uiteindelijk loop je vast.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1117" data-mp="81g104d3mhp0">
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1118" data-mp="2835w6k5okby5">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1119" data-mp="2d2idyqhp2stv">
                <ShieldCheckIcon className="w-6 h-6 text-accent" data-id="element-1120" data-mp="2h2arzipuxr6m" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1121" data-mp="2m3887prb4b5h">
                Beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1122" data-mp="2r0gt3y3uataf">
                Zonder updates wordt je website kwetsbaar voor hackers en
                malware. Wij houden je website veilig.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1123" data-mp="30t612j06lp10">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1124" data-mp="34uhii18wj3y4">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1125" data-mp="39pwwquatn1v2" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1126" data-mp="3eoq9gkiaaou6">Backups</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1127" data-mp="3owce0s0hd5t6">
                Dagelijkse backups zorgen ervoor dat je nooit data verliest. Bij
                problemen zetten we alles snel terug.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1128" data-mp="3eyepe3epl701">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1129" data-mp="bfv6hrhx7dhw">
                <RefreshCwIcon className="w-6 h-6 text-accent" data-id="element-1130" data-mp="6h1te06jpoi9" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1131" data-mp="6j4i8gvypble">Updates</h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1132" data-mp="1o8qlhun0q8t">
                WordPress, thema's en plugins hebben regelmatig updates nodig om
                goed en veilig te blijven werken.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft hover:shadow-medium transition-shadow" data-id="element-1133" data-mp="pfnwfklmhpot">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1134" data-mp="193y7c6wqunyy">
                <ActivityIcon className="w-6 h-6 text-accent" data-id="element-1135" data-mp="1duwpzd57fy5d" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1136" data-mp="z5ih065jjiqi">
                Monitoring
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1137" data-mp="14421s38eeoha">
                Wij monitoren je website 24/7 op uptime en prestaties. Problemen
                lossen we op voordat jij ze merkt.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Wat inbegrepen is */}
      <section id="pakketten" className="py-24 bg-white" data-id="element-1138" data-mp="1hwhy6l6sno98">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1139" data-mp="1hy2rk7hdh15i">
          <div className="text-center mb-16" data-id="element-1140" data-mp="1mtrv0lwij273">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1141" data-mp="6gtafrlpul7d">
              Wat zit er in ons onderhoudspakket?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1142" data-mp="bgz9hsubf9zh">
              Alles wat je nodig hebt voor een zorgeloze website, standaard
              inbegrepen.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1143" data-mp="2knstfx28hejn">
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1144" data-mp="2yovj0jsft6zb">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1145" data-mp="2un1lulhgal3w">
                <PuzzleIcon className="w-6 h-6 text-navy-700" data-id="element-1146" data-mp="26wnz9ahmxegp" />
              </div>
              <div data-id="element-1147" data-mp="21ycrr6etbg1n">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1148" data-mp="21yw0pxmhiu3j">
                  Plugin updates
                </h3>
                <p className="text-slate-600" data-id="element-1149" data-mp="2fqasasugk3m7">
                  Alle plugins en thema's worden maandelijks bijgewerkt naar de
                  nieuwste versie.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1150" data-mp="1n7djxxlswvj4">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1151" data-mp="1j7cxpqsgng1i">
                <HardDriveIcon className="w-6 h-6 text-navy-700" data-id="element-1152" data-mp="1h0l4ofnzciyj" />
              </div>
              <div data-id="element-1153" data-mp="1c4dxkjjul1f0">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1154" data-mp="1c1rt4ss97210">
                  Dagelijkse backups
                </h3>
                <p className="text-slate-600" data-id="element-1155" data-mp="133fty2dbmvwy">
                  Elke dag wordt er automatisch een volledige backup gemaakt van
                  je website en database.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1156" data-mp="tcbdey7zz300">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1157" data-mp="of1rhsjth9gx">
                <EyeIcon className="w-6 h-6 text-navy-700" data-id="element-1158" data-mp="kdq1vn0y0o8q" />
              </div>
              <div data-id="element-1159" data-mp="fcadbkzk3zll">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1160" data-mp="fcsjb9c0ts6p">
                  Security monitoring
                </h3>
                <p className="text-slate-600" data-id="element-1161" data-mp="aglb19w5k4ll">
                  Continue bewaking op malware, verdachte activiteit en
                  ongeautoriseerde toegangspogingen.
                </p>
              </div>
            </div>
            <div className="border border-navy-100 p-8 rounded-card flex items-start gap-5" data-id="element-1162" data-mp="2vz8n91fphlyz">
              <div className="w-12 h-12 rounded-xl bg-navy-50 flex items-center justify-center flex-shrink-0" data-id="element-1163" data-mp="3013lty695l92">
                <GaugeIcon className="w-6 h-6 text-navy-700" data-id="element-1164" data-mp="23bmhpnmx479h" />
              </div>
              <div data-id="element-1165" data-mp="289xti3wrwruc">
                <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1166" data-mp="289ecqqiv09hw">
                  Performance optimalisatie
                </h3>
                <p className="text-slate-600" data-id="element-1167" data-mp="2cahqn7hcjvgh">
                  Regelmatige controle en optimalisatie van laadsnelheid,
                  caching en database.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hosting voordelen */}
      <section className="py-24 bg-navy-50" data-id="element-1168" data-mp="1okndspx126w8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1169" data-mp="2rwugwwj3okq6">
          <div className="text-center mb-16" data-id="element-1170" data-mp="2rv9opdk9ddvh">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1171" data-mp="2myj90rcd8kal">
              Betrouwbare hosting voor jouw website
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-1172" data-mp="31m47g7yapvc6">
              Onze hosting is speciaal geoptimaliseerd voor WordPress websites.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1173" data-mp="28z1dzu8h8ass">
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1174" data-mp="23zopz65vcxom">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1175" data-mp="241i1ozvca83h">
                <ZapIcon className="w-6 h-6 text-accent" data-id="element-1176" data-mp="2dxcmo1n1cf9f" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1177" data-mp="2dzfmfddzpmrc">
                Snelle servers
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1178" data-mp="3pci46brtsid3">
                Onze servers zijn geoptimaliseerd voor WordPress. Jouw website
                laadt razendsnel, altijd.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1179" data-mp="5pg3xkh9a7z7">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1180" data-mp="5nn0sf5uq5vy">
                <DatabaseIcon className="w-6 h-6 text-accent" data-id="element-1181" data-mp="3n07ghfa17cbt" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1182" data-mp="3rz0mynqwi32h">
                Dagelijkse backups
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1183" data-mp="og7m9bj818pq">
                Automatische dagelijkse backups met één-klik herstel. Je data is
                altijd veilig.
              </p>
            </div>
            <div className="bg-white p-8 rounded-card shadow-soft" data-id="element-1184" data-mp="eq4n0tliayiy">
              <div className="w-12 h-12 rounded-xl bg-accent-light flex items-center justify-center mb-6" data-id="element-1185" data-mp="eobbazw1do43">
                <LockIcon className="w-6 h-6 text-accent" data-id="element-1186" data-mp="12e5oxjo6jgpe" />
              </div>
              <h3 className="text-xl font-bold text-navy-900 mb-3" data-id="element-1187" data-mp="17804qc0nzbj7">
                SSL beveiliging
              </h3>
              <p className="text-slate-600 leading-relaxed" data-id="element-1188" data-mp="3flf1gdfypbi1">
                Gratis SSL-certificaat inbegrepen. Je website is beveiligd en
                Google waardeert dat.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde pagina's */}
      <section className="py-16 bg-surface-muted border-y border-navy-100" data-id="element-1189" data-mp="30sln4vcff1ey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1190" data-mp="28bzeu1d48if">
          <h3 className="text-lg font-semibold text-navy-900 mb-6" data-id="element-1191" data-mp="3tg7mtsij9lwj">
            Ontdek meer:
          </h3>
          <div className="flex flex-wrap justify-center gap-4" data-id="element-1192" data-mp="3on52maznbot0">
            <a href="#/website-laten-maken" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1193" data-mp="3jpmuunxd7fml">
              Nieuwe website laten bouwen
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1194" data-mp="g7utwk193lc9">•</span>
            <a href="#/wordpress-websites" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1195" data-mp="ohsfk5qik1sc">
              WordPress specialist
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1196" data-mp="xgxh7kxdgb8q">•</span>
            <a href="#/website-laten-maken-checklist" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1197" data-mp="12gs1v6qk04lc">
              Checklist voor je website
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-1198" data-mp="1bg6uabqa6p8h">•</span>
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-1199" data-mp="1g86odnjo6nx5">
              Terug naar homepage
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-white" data-id="element-1200" data-mp="1q4tasgrn9n1a">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1201" data-mp="1v6sbfiw025uj">
          <div className="text-center mb-16" data-id="element-1202" data-mp="15hbugs7w1tst">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-1203" data-mp="15id36q1fm47d">
              Veelgestelde vragen over website onderhoud
            </h2>
          </div>

          <div className="space-y-4" data-id="element-1204" data-mp="1f7wm1vp59y28">
            {faqs.map((faq, index) => <div key={index} className="bg-surface-muted rounded-xl shadow-sm border border-navy-50 overflow-hidden" data-id="element-1205" data-mp="1agy6wzfw760k" data-mpi={index}>
                <button onClick={() => toggleFaq(index)} className="w-full px-6 py-5 flex items-center justify-between text-left focus:outline-none" data-id="element-1206" data-mp="1oa5zatbss5jb">
                  <span className="font-bold text-navy-900" data-id="element-1207" data-mp="1y46a9btrg4zy">{faq.q}</span>
                  {openFaq === index ? <ChevronUpIcon className="w-5 h-5 text-accent flex-shrink-0" data-id="element-1208" data-mp="3s82leb3nh66" /> : <ChevronDownIcon className="w-5 h-5 text-navy-400 flex-shrink-0" data-id="element-1209" data-mp="3uysnsheh3uva" />}
                </button>
                {openFaq === index && <div className="px-6 pb-5 text-slate-600 leading-relaxed" data-id="element-1210" data-mp="2ail4e0jxncbi">
                    {faq.a}
                  </div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-1211" data-mp="25p05856lscaq" />
    </main>;
}